/**
 * <sy-carousel> — Sunayla scroll-snap carousel.
 *
 * Progressive enhancement: the track scrolls and snaps with CSS alone.
 * This module only wires up the prev/next buttons, the dots and keyboard
 * support, and marks the host `is-ready` so the controls become visible.
 *
 * No autoplay, ever. Motion respects prefers-reduced-motion.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class SyCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-sy-track]');
    this.slides = Array.from(this.querySelectorAll('[data-sy-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-sy-dot]'));
    this.prevButton = this.querySelector('[data-sy-prev]');
    this.nextButton = this.querySelector('[data-sy-next]');

    if (!this.track || this.slides.length < 2) return;

    this.index = 0;

    this.onPrev = () => this.go(this.index - 1);
    this.onNext = () => this.go(this.index + 1);
    this.onScroll = () => this.scheduleSync();
    this.onKeydown = (event) => this.handleKeydown(event);

    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('keydown', this.onKeydown);

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.go(i));
    });

    this.classList.add('is-ready');
    this.sync();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.onPrev);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('keydown', this.onKeydown);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(this.index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(this.index + 1);
    }
  }

  /** Move to a slide, wrapping at both ends. */
  go(target) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    const slide = this.slides[next];
    if (!slide) return;

    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });

    this.index = next;
    this.paint();
  }

  scheduleSync() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.sync());
  }

  /** Work out which slide is closest to the viewport centre. */
  sync() {
    const centre = this.track.scrollLeft + this.track.clientWidth / 2;
    let closest = 0;
    let smallest = Infinity;

    this.slides.forEach((slide, i) => {
      const slideCentre = slide.offsetLeft - this.track.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(slideCentre - centre);
      if (distance < smallest) {
        smallest = distance;
        closest = i;
      }
    });

    this.index = closest;
    this.paint();
  }

  paint() {
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    // Slides are never aria-hidden: every slide stays scrollable and its link
    // stays focusable, so hiding them from assistive tech would strand focus.
  }
}

if (!customElements.get('sy-carousel')) {
  customElements.define('sy-carousel', SyCarousel);
}
